import type {
  TTicketingEscalationField,
  TTicketingFieldError,
} from '@cascade/api-client/shared';
import type {
  TSplitFieldErrors,
  TTicketFieldErrors,
  TTicketFieldValues,
} from './CreateTicketDialog.types.ts';
import { isOverLength } from './ticket-field/index.ts';

const BOOLEAN_TYPE = 'boolean';
const TRUE_VALUE = 'true';
const optionTypes = ['choice', 'reference'];

const isEditable = (field: TTicketingEscalationField): boolean =>
  field.editable !== false;

const hasNoOptions = (field: TTicketingEscalationField): boolean =>
  optionTypes.includes(field.type ?? '') && field.values.length === 0;

export const buildInitialValues = (
  fields: TTicketingEscalationField[],
): TTicketFieldValues =>
  Object.fromEntries(
    fields.map((field) => [
      field.key,
      field.type === BOOLEAN_TYPE
        ? field.default === TRUE_VALUE
        : (field.default ?? ''),
    ]),
  );

export const hasMissingRequiredField = (
  fields: TTicketingEscalationField[],
  values: TTicketFieldValues,
): boolean =>
  fields
    .filter(isEditable)
    .filter((field) => field.required && field.type !== BOOLEAN_TYPE)
    .filter((field) => !hasNoOptions(field))
    .some((field) => {
      const value = values[field.key];

      return typeof value !== 'string' || value.trim().length === 0;
    });

export const hasOverLengthField = (
  fields: TTicketingEscalationField[],
  values: TTicketFieldValues,
): boolean =>
  fields
    .filter(isEditable)
    .some((field) => isOverLength(field, values[field.key]));

export const buildCustomFields = (
  fields: TTicketingEscalationField[],
  values: TTicketFieldValues,
): Record<string, unknown> | undefined => {
  const entries = fields.filter(isEditable).map((field) => {
    const value = values[field.key];

    return [field.key, typeof value === 'string' ? value.trim() : value];
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

export const splitFieldErrors = (
  fields: TTicketingEscalationField[],
  fieldErrors: TTicketingFieldError[],
): TSplitFieldErrors => {
  const keys = new Set(fields.map((field) => field.key));
  const known: TTicketFieldErrors = {};
  const unknown: string[] = [];

  fieldErrors.forEach(({ key, message }) => {
    if (keys.has(key)) {
      known[key] = message;

      return;
    }

    unknown.push(`${key}: ${message}`);
  });

  return { known, unknown };
};
